import type { LucideIcon } from "lucide-react";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { FloatPanel } from "@/features/studio/theme";

interface StatCardBaseProps {
	icon: LucideIcon;
	label: string;
	subValue?: string;
	trend?: {
		value: string;
		label: string;
		isPositive?: boolean;
	};
	chart?: React.ReactNode;
	className?: string;
}

/** The query behind the tile failed, and the tile offers a retry in its place. */
interface StatCardError {
	message: string;
	onRetry: () => void;
	isRetrying?: boolean;
}

/**
 * A tile shows either a value or a failure, never both and never neither: a
 * failed read must not be reported as a number, and a tile with no `value` and
 * no `error` would render an empty figure.
 */
type StatCardProps = StatCardBaseProps &
	(
		| { value: string | number; error?: never }
		| { value?: never; error: StatCardError }
	);

export function StatCard({
	icon: Icon,
	label,
	value,
	subValue,
	error,
	trend,
	chart,
	className,
}: StatCardProps) {
	return (
		<FloatPanel
			elevation={1}
			padding={18}
			className={className}
			style={{ background: "var(--color-dashboard-paper)" }}
		>
			<div className="flex items-start justify-between">
				<div className="flex-1">
					<div className="flex items-center gap-1.5 text-[var(--color-dashboard-muted)]">
						<Icon className="h-3.5 w-3.5" />
						<span
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 10,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}
						>
							{label}
						</span>
					</div>
					{error ? (
						<QueryErrorState
							className="py-3"
							message={error.message}
							onRetry={error.onRetry}
							isRetrying={error.isRetrying}
						/>
					) : (
						<div className="mt-2.5">
							<div
								style={{
									fontFamily: "var(--font-display)",
									fontSize: 22,
									fontWeight: 700,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.02em",
								}}
							>
								{value}
							</div>
							{subValue && (
								<div
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 12,
										color: "var(--color-dashboard-muted)",
										marginTop: 4,
									}}
								>
									{subValue}
								</div>
							)}
						</div>
					)}
					{trend && (
						<div className="mt-2 flex items-center gap-1">
							<span
								className={
									trend.isPositive
										? "text-dashboard-pill-done"
										: "text-dashboard-pill-risk"
								}
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									fontWeight: 600,
									color: trend.isPositive
										? "var(--color-dashboard-pill-done)"
										: "var(--color-dashboard-pill-risk)",
								}}
							>
								{trend.value}
							</span>
							<span
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 10,
									color: "var(--color-dashboard-muted)",
								}}
							>
								{trend.label}
							</span>
						</div>
					)}
				</div>
				{chart && <div className="ml-3">{chart}</div>}
			</div>
		</FloatPanel>
	);
}
